/**
 * Component Registry for Weidner Apartment Homes Rise Theme
 * 
 * Extends the base rise theme registry with Weidner Apartment Homes Rise specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-rise/registry';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { AmenitiesList } from './components/AmenitiesList';
import { ApplyNow } from './components/ApplyNow';
import { ContactForm } from './components/ContactForm';
import { FloorplansList } from './components/FloorplansList';
import { Footer } from './components/Footer';
import { FutureResidentsClub } from './components/FutureResidentsClub';
import { GallerySeparator } from './components/GallerySeparator';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { LuxuryDefinition } from './components/LuxuryDefinition';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { Penthouse } from './components/Penthouse';
import { PetPolicy } from './components/PetPolicy';
import { PhotoGallery } from './components/PhotoGallery';
import { PinnedCallouts } from './components/PinnedCallouts';
import { PricingVerbiage } from './components/PricingVerbiage';
import { Reviews } from './components/Reviews';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { TabbedCallouts } from './components/TabbedCallouts';

/**
 * Weidner Apartment Homes Rise specific component overrides.
 * These components have been customized for the Weidner Apartment Homes Rise brand.
 *
 * - `Footer`: four-column Weidner Classic layout (contact · compliance /
 *   managed-by · office hours · logo + Google review) with an identity-
 *   verification + copyright (center) / powered-by (left) / design-by
 *   (right) bar. Keeps Weidner-only Text Us!, CORT CTA,
 *   social icons, and the RentCafe IQ z-index pin. See
 *   `src/components/Footer.tsx` for details.
 * - `Header`: forked from the rise base to surface the Home page in the
 *   main navigation. The rise base intentionally hides `home` from the
 *   dynamic nav (the logo serves as the home affordance), but Weidner
 *   wants an explicit "Home" link alongside the other top-level pages.
 *   The fork drops `'home'` from `NAV_EXCLUDED_ALIASES`, merges local
 *   `src/pages.json` overrides with the rise page list, and groups
 *   pages under placeholder dropdowns: Find Your Home, High-Rise Living,
 *   and Contact (not real pages).
 * - `FutureResidentsClub`: standard Weidner Future Residents Club layout
 *   shared with Belmont, Oasis, Vantage, Blue Haven, Highland, and Via6
 *   (centered intro, 3×2 icon benefits grid, How It Works split with
 *   APPLY NOW CTA + property photo), styled with Weidner Classic mustard
 *   accents. Wired into the `future-residents-club` page in
 *   `src/pages.json` under HeroSolidBG.
 * - `Hero`: forked from the rise base to drop the pre-title and CTA,
 *   enlarge the title, remove the title glow, and anchor the copy lower
 *   on the hero image. See the header comment in `src/components/Hero.tsx`
 *   for the maintenance checklist.
 * - `InteractiveMapSection`: Building View map. Uses the Engrain
 *   Sightmap when the property has a custom Engrain sitemap id or
 *   embed URL; otherwise keeps the ApartmentSync interactive map.
 * - `HeroSolidBG`: Tower 12 interior-page banner (photo, 50% black
 *   overlay, centered white uppercase title, and an optional white
 *   page blurb). Same slot / `tokenPrefix` instances so every interior
 *   page inherits the treatment. Blurbs are opt-in via `showBlurb` in
 *   `pages.json`, bound to the page's property blurb when one exists.
 *   An optional mustard CTA (`showCta`) is used on Virtual Tours.
 *   Background image is static (no parallax scroll). See the header
 *   comment in `src/components/HeroSolidBG.tsx` for the maintenance
 *   checklist.
 * - `PetPolicy`: standard Weidner pet-policy layout shared with the
 *   other Weidner themes — left column (paw welcome badge, description,
 *   restrictions box with the standard breed-list fallback, View
 *   Amenities CTA), right column (2-up stat cards). Classic mustard
 *   labels and CTA. See the header comment in
 *   `src/components/PetPolicy.tsx` for the maintenance checklist.
 * - `AccessibilityStatement`: mirrors the rise base except that the
 *   embedded AudioEye portal iframe is removed. On Weidner domains the
 *   portal renders an "AudioEye is currently inactive on this website"
 *   notice, so the iframe (and the `property_domain` lookup that fed its
 *   src) is dropped; the sr-only heading, rich-text statement body, and
 *   editable "Contact Us" link are unchanged. See the header comment in
 *   `src/components/AccessibilityStatement.tsx` for the maintenance
 *   checklist.
 * - `ContactForm`: Tower 12 contact body (https://www.tower12.com/contact-us)
 *   — white two-column section with gray "HAVE QUESTIONS" display headline,
 *   uppercase lead, left-column lead form, and right-column property map.
 *   Drops the rise centered parallax form card and info-card row. See
 *   the header comment in `src/components/ContactForm.tsx`.
 * - `ScheduleTourForm`: Tower 12 schedule body
 *   (https://www.tower12.com/schedule-a-tour) — light gray section with a
 *   centered white Multihub tour-scheduler card and gold accents. Drops
 *   the rise parallax form card, PropertyInfoPanel row, and ContactMap.
 *   See the header comment in `src/components/ScheduleTourForm.tsx`.
 * - `GallerySeparator`: mirrors the rise base except that the headline
 *   column no longer overlaps the featured image on the right. The
 *   parent pulls the image ~6rem into the title column (and paints a
 *   left-edge gradient on the image so the overlapping text stays
 *   legible); the fork removes the negative margin, adds a small gutter
 *   between the columns, and drops the now-unneeded gradient. See the
 *   header comment in `src/components/GallerySeparator.tsx` for the
 *   maintenance checklist.
 * - `PricingVerbiage`: adapted from the preferred theme. Renders
 *   `custom_pricing_verbiage` as centered muted richtext above the
 *   ApartmentSync floor-plan embed. Hidden when the field is empty.
 * - `FloorplansList`: Tower-12-style icon category row (line-art plan
 *   glyphs with letter/number badges, vertical dividers, dotted
 *   background, primary "View All" CTA with dark label text).
 * - `TabbedCallouts`: replaces the rise tabbed panels with a two-column
 *   showcase — left photo-gallery slideshow (offset gray frame with
 *   white gap) and right copy column (vertical rule, property heading,
 *   subhead, body, primary CTA).
 * - `PinnedCallouts`: Tower 12 fixed-background parallax — image is
 *   position:fixed (pinned to the viewport) and clipped to the section;
 *   foreground copy scrolls over it (left translucent panel, open right).
 * - `LuxuryDefinition`: “definition of luxury” split — left property
 *   graphic (designed mark when the property has one, otherwise a
 *   gallery photo), right 10px rule + 100px gray LUXURY, location
 *   body, and mustard gallery CTA on a tiled dotted ground.
 * - `SpecialOfferBanner`: Belmont offer behavior (rotation, dismiss,
 *   scroll-to-pill, pill re-opens the banner) on Weidner black/gold
 *   chrome with a Contact Us link. See
 *   `src/components/SpecialOfferBanner.tsx`.
 * - `AmenitiesList`: Tower 12 amenities page — centered heading, 4-up
 *   square photo row, and 4-column disc-bullet list for Community then
 *   Apartment, plus a mustard CTA under each block. Replaces the
 *   rise slider + paginated two-column rows.
 * - `Penthouse`: Tower 12 penthouse page — two-column layout with
 *   EXPERIENCE / PENTHOUSE LIVING + gallery + features on the left and
 *   a plan card (price, floor-plan image, Schedule Tour /
 *   Contact Us) on the right.
 * - `PhotoGallery`: Tower 12 gallery page — ALL / Video / Community
 *   Amenities / Apartment Amenities filter tabs over a 3-column media
 *   grid with lightbox. Replaces the rise apartments + separator +
 *   community stack on the gallery page.
 * - `NeighborhoodMap`: Tower 12 neighborhood page — centered subtitle
 *   + muted blurb, then Front / South / East view tabs over a
 *   panoramic hotspot image with pulsing pins. Replaces the rise
 *   Google Maps + POI card list.
 * - `Reviews`: Tower 12 resident-reviews feed
 *   (https://www.tower12.com/resident-reviews) — centered
 *   "What our customers say" heading, Google summary bar, and a
 *   vertical review feed with avatars, Read more, and Google
 *   attribution. Page hero + `property_reviews_blurb` stay on
 *   HeroSolidBG in `pages.json`. Replaces the rise 3-column card grid.
 * - `ApplyNow`: same two-card body as rise Resident Access, for the
 *   Apply Now page — apply-online CTA (`property_apply_now_url`) plus
 *   phone help. Wired under HeroSolidBG on `apply-now` in pages.json.
 */
const weidnerapartmenthomesriseOverrides: Partial<ComponentRegistry> = {
  AccessibilityStatement,
  AmenitiesList,
  ApplyNow,
  ContactForm,
  FloorplansList,
  Footer,
  FutureResidentsClub,
  GallerySeparator,
  Header,
  Hero,
  HeroSolidBG,
  InteractiveMapSection,
  LuxuryDefinition,
  NeighborhoodMap,
  Penthouse,
  PetPolicy,
  PhotoGallery,
  PinnedCallouts,
  PricingVerbiage,
  Reviews,
  ScheduleTourForm,
  SpecialOfferBanner,
  TabbedCallouts,
};

/**
 * Final merged registry for Weidner Apartment Homes Rise properties.
 * Inherits all components from the rise theme, with Weidner Apartment Homes Rise overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesriseOverrides,
};

export default registry;
